import React, { useEffect, useState } from 'react'
import api from 'api/api'
import WithTooltip from 'components/shared/WithTooltip'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import { Bitcoin } from 'lucide-react'
import styles from './tdp.module.scss'
import useStore from 'state/knovStore'
import { SATOSHIS } from 'lib/bsv-util'
import { pollTdp } from './tdpPolling'

type BsvTdpResponse = {
    total_bsv: number
    computed_on: string
    breakdown?: {
        upvalues_bsv?: number
        ai_fee_sats?: number
        ordinal_mint_fee_sats?: number
    }
}

function formatBsv(value: number) {
    if (!Number.isFinite(value)) return '—'
    const decimals = value >= 100 ? 0 : value >= 1 ? 2 : 4
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: decimals }).format(value)
}

export default function TDP() {
    const [data, setData] = useState<BsvTdpResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const openTdpViewerModal = useStore(state => state.actions.modalActions.openTdpViewerModal)
    const { useUsd, formatSatsAmount } = useBsvDisplay()

    useEffect(() => {
        // Server value is cached for 10m; refresh every 5m to keep it reasonably fresh.
        return pollTdp(async () => {
            const res = (await api.getBsvTdp({ _t: Date.now() })) as BsvTdpResponse | null
            setLoading(false)
            return res
        }, setData)
    }, [])

    const valueText = data
        ? useUsd
            ? formatSatsAmount(data.total_bsv * SATOSHIS, {
                  includeUnit: false,
                  compactSats: false,
              })
            : `${formatBsv(data.total_bsv)}`
        : '—'

    return (
        <WithTooltip tip={'View TDP details'} html>
            <div
                className={styles.container}
                role="button"
                tabIndex={0}
                aria-label="Open TDP details"
                onClick={e => {
                    e.stopPropagation()
                    openTdpViewerModal()
                }}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        openTdpViewerModal()
                    }
                }}
            >
                {/* USD values already carry their own "$" prefix. */}
                {!useUsd && (
                    <Bitcoin
                        className={`${styles.coinIcon} ${styles.bitcoinIcon}`}
                        size={12}
                        aria-hidden
                    />
                )}
                <div className={`${styles.value} ${loading ? styles.loading : ''}`}>
                    {valueText}
                </div>
                <div className={styles.label}>TDP</div>
            </div>
        </WithTooltip>
    )
}
